import { Box, Button, CircularProgress, InputLabel, MenuItem, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPlanLlmPrompt, DialogPlanLlmPromptApi, DialogPlanLlmPromptMatchGame } from "../../api/DialogPlanLlmPromptApi";
import { MatchEnum } from "../../api/DialogPromptApi";
import AppBarContext from '../../context/AppBarContext';
import { handleErrorMessage } from '../ErrorDialog';

interface DialogPlanLlmPromptMatchGameFormProps {
    dialogPlanLlmPromptId?: number;
    dialogPlanLlmPrompt?: DialogPlanLlmPromptMatchGame;
    setDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleLlmPromptChange?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleRegenerateDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt, fieldName: string) => void;
    isRegenerating?: boolean;

    id: number;
    dialogPlanId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    yesResponse: string;
    noResponse: string;
    // imageSource: string;
}

export const DialogPlanLlmPromptMatchGameForm: React.FC<DialogPlanLlmPromptMatchGameFormProps> = ({
    dialogPlanLlmPromptId, dialogPlanLlmPrompt, setDialogPlanLlmPrompt, handleLlmPromptChange, handleRegenerateDialogPlanLlmPrompt, isRegenerating,
    id, dialogPlanId, orderIndex, isTeacher, prompt, isNoteToStudent, yesResponse, noResponse }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [vocabularyWordList, setVocabularyWordList] = React.useState<string | null>(null);
    const [vocabularyWordAudioSentenceList, setVocabularyWordAudioSentenceList] = React.useState<string | null>(null);
    const [vocabularyWordImageList, setVocabularyWordImageList] = React.useState<string | null>(null);
    const [vocabularyWordEnglishTextList, setVocabularyWordEnglishTextList] = React.useState<string | null>(null);

    const [leftMatch, setLeftMatch] = React.useState(MatchEnum.FirstWordCharacter);
    const [rightMatch, setRightMatch] = React.useState(MatchEnum.FirstWordPinyin);

    const handleVocabularyWordListChange = (e: React.ChangeEvent<HTMLInputElement>) => { setVocabularyWordList(e.target.value); };
    const handleVocabularyWordAudioSentenceListChange = (e: React.ChangeEvent<HTMLInputElement>) => { setVocabularyWordAudioSentenceList(e.target.value); };
    const handleVocabularyWordImageListChange = (e: React.ChangeEvent<HTMLInputElement>) => { setVocabularyWordImageList(e.target.value); };
    const handleVocabularyWordEnglishTextListChange = (e: React.ChangeEvent<HTMLInputElement>) => { setVocabularyWordEnglishTextList(e.target.value); };
    const handleLeftMatchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setLeftMatch(e.target.value as MatchEnum); };
    const handleRightMatchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setRightMatch(e.target.value as MatchEnum); };
    // const handleDisplayPinyinChange = (e: React.ChangeEvent<HTMLInputElement>) => { setDisplayPinyin(e.target.checked); };

    React.useEffect(() => {
        if (!dialogPlanLlmPromptId && dialogPlanLlmPrompt) {
            setDialogPlanLlmPromptFields(dialogPlanLlmPrompt);
        }
    }, [dialogPlanLlmPrompt, dialogPlanLlmPromptId])
    React.useEffect(() => {
        if (dialogPlanLlmPromptId && !dialogPlanLlmPrompt) {
            DialogPlanLlmPromptApi.getById(dialogPlanLlmPromptId)
                .then((response) => {
                    const dialogPlanLlmPrompt = response.data as DialogPlanLlmPromptMatchGame;
                    setDialogPlanLlmPromptFields(dialogPlanLlmPrompt);
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    }, [dialogPlanLlmPromptId, dialogPlanLlmPrompt, history, location, setErrorMessage])

    function setDialogPlanLlmPromptFields(dialogPlanLlmPrompt: DialogPlanLlmPromptMatchGame) {
        if (dialogPlanLlmPrompt.class_name == "DialogPlanLlmPromptMatchGameModel") {
            setVocabularyWordList(dialogPlanLlmPrompt.vocabulary_word_list);
            setVocabularyWordAudioSentenceList(dialogPlanLlmPrompt.vocabulary_word_audio_sentence_list);
            setVocabularyWordImageList(dialogPlanLlmPrompt.vocabulary_word_image_list);
            setVocabularyWordEnglishTextList(dialogPlanLlmPrompt.vocabulary_word_english_text_list);
            setLeftMatch(dialogPlanLlmPrompt.left_match);
            setRightMatch(dialogPlanLlmPrompt.right_match);
        }
    }

    const currentDialogPlanLlmPrompt = React.useMemo(() => (
        new DialogPlanLlmPromptMatchGame(
            id,
            dialogPlanId,
            orderIndex,
            isTeacher,
            prompt,
            isNoteToStudent,
            yesResponse,
            noResponse,
            vocabularyWordList ?? dialogPlanLlmPrompt?.vocabulary_word_list ?? '',
            vocabularyWordAudioSentenceList ?? dialogPlanLlmPrompt?.vocabulary_word_audio_sentence_list ?? '',
            vocabularyWordImageList ?? dialogPlanLlmPrompt?.vocabulary_word_image_list ?? '',
            vocabularyWordEnglishTextList ?? dialogPlanLlmPrompt?.vocabulary_word_english_text_list ?? '',
            leftMatch,
            rightMatch,
        )
    ), [
        id, dialogPlanId, orderIndex, isTeacher, prompt, isNoteToStudent, yesResponse, noResponse,
        dialogPlanLlmPrompt?.vocabulary_word_list,
        dialogPlanLlmPrompt?.vocabulary_word_audio_sentence_list,
        dialogPlanLlmPrompt?.vocabulary_word_image_list,
        dialogPlanLlmPrompt?.vocabulary_word_english_text_list,
        vocabularyWordList, vocabularyWordAudioSentenceList, vocabularyWordImageList, vocabularyWordEnglishTextList,
        leftMatch, rightMatch,
    ]);

    React.useEffect(() => {
        if (handleLlmPromptChange) {
            handleLlmPromptChange(currentDialogPlanLlmPrompt);
        }
        if (setDialogPlanLlmPrompt) {
            setDialogPlanLlmPrompt(currentDialogPlanLlmPrompt);
        }
    }, [currentDialogPlanLlmPrompt, handleLlmPromptChange, setDialogPlanLlmPrompt])

    return (<>
        <Stack spacing={4} sx={{
            width: dialogPlanLlmPrompt ? '100%' : '90%', // Conditionally set width
            margin: '0 auto', pt: 2, pb: 4
        }}>
            <InputLabel id="left-match-label">Left Match</InputLabel>
            <TextField
                select
                value={leftMatch}
                onChange={(e) => handleLeftMatchChange(e)}
                fullWidth
            >
                {Object.entries(MatchEnum).map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                        {key}
                    </MenuItem>
                ))}
            </TextField>

            <InputLabel id="right-match-label">Right Match</InputLabel>
            <TextField
                select
                value={rightMatch}
                onChange={(e) => handleRightMatchChange(e)}
                fullWidth
            >
                {Object.entries(MatchEnum).map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                        {key}
                    </MenuItem>
                ))}
            </TextField>

            <h3>Answer List</h3>
            <TextField
                label="LLM Prompt for first answer list"
                value={vocabularyWordList ?? ''}
                onChange={handleVocabularyWordListChange}
                multiline
                rows={5}
            />
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(currentDialogPlanLlmPrompt, "vocabulary_word_list")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}



            <TextField
                label="LLM Prompt for second answer list (with audio)"
                value={vocabularyWordAudioSentenceList ?? ''}
                onChange={handleVocabularyWordAudioSentenceListChange}
                multiline
                rows={5}
            />
            <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                &lt;VocabularyWord/&gt; will be replaced with
                the corresponding correct vocabulary word when generating.
            </Typography>
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(currentDialogPlanLlmPrompt, "vocabulary_word_audio_sentence_list")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}



            <TextField
                label="LLM Prompt for matching image"
                value={vocabularyWordImageList ?? ''}
                onChange={handleVocabularyWordImageListChange}
                multiline
                rows={5}
            />
            <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                &lt;VocabularyWord/&gt; will be replaced with
                the corresponding correct vocabulary word when generating.
            </Typography>
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(currentDialogPlanLlmPrompt, "vocabulary_word_image_list")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}




            <TextField
                label="LLM Prompt for english answer list"
                value={vocabularyWordEnglishTextList ?? ''}
                onChange={handleVocabularyWordEnglishTextListChange}
                multiline
                rows={5}
            />
            <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                &lt;VocabularyWord/&gt; will be replaced with
                the corresponding correct vocabulary word when generating.
            </Typography>
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(currentDialogPlanLlmPrompt, "vocabulary_word_english_text_list")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}

        </Stack>
    </>);
};
